import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiProcesos } from "@/componentes/kpi/KpiPlanificacion"
import { documentoDeProcesos } from "@/componentes/procesos/exportarProcesos"
import { FichaProceso } from "@/componentes/procesos/FichaProceso"
import { ListaProcesos } from "@/componentes/procesos/ListaProcesos"
import type { CargoElegible, Proceso } from "@/componentes/procesos/tipos"
import { Modal } from "@/componentes/ui/Modal"

/**
 * El flujo operativo: qué se hace, en qué orden y quién responde por cada paso.
 *
 * ⚠ **Esta pantalla existe para que el descriptivo de cargo se arme solo.** Las
 * funciones de un puesto no se escriben dos veces: se describen aquí una vez como
 * pasos del proceso, se le asigna a cada paso su cargo responsable, y el descriptivo
 * —en el catálogo de cargos— es la lectura inversa.
 *
 * ⚠ **El título del paso se redacta como saldrá impreso.** «Verificar el arancel del
 * despacho», no «Arancel»: es el texto que acabará en un contrato de trabajo, y la
 * pantalla lo dice para que no haya que descubrirlo leyendo el contrato.
 *
 * ⚠ **Es una lista ordenada, no un lienzo.** Un paso de decisión anota sus dos ramas
 * como texto. Dibujar un grafo de verdad —aristas, ciclos, arrastre en un canvas— es
 * otro proyecto, y para lo que esto tiene que contestar —«¿qué hace este cargo?»— el
 * orden basta.
 *
 * La lista está en `componentes/procesos/ListaProcesos.tsx` y la ficha con sus pasos
 * en `FichaProceso.tsx`.
 */
export function Procesos() {
  const clienteQuery = useQueryClient()
  const [elegido, setElegido] = useState<number | null>(null)
  const [creando, setCreando] = useState(false)
  const [nombreNuevo, setNombreNuevo] = useState("")
  /** "" = todas; "sin" = las que no tienen área; si no, el id del área. */
  const [area, setArea] = useState("")

  const procesos = useQuery({
    queryKey: ["procesos"],
    queryFn: () => api.get<Proceso[]>("/api/v1/procesos"),
  })
  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<CargoElegible[]>("/api/v1/cargos?solo_activos=true"),
  })

  const proceso = procesos.data?.find((p) => p.id === elegido) ?? procesos.data?.[0]

  const crear = useMutation({
    mutationFn: () => api.post<Proceso>("/api/v1/procesos", { nombre: nombreNuevo }),
    onSuccess: (nuevo) => {
      clienteQuery.invalidateQueries({ queryKey: ["procesos"] })
      clienteQuery.invalidateQueries({ queryKey: ["descriptivo"] })
      setCreando(false)
      setNombreNuevo("")
      setElegido(nuevo.id)
    },
  })

  if (procesos.isPending) {
    return (
      <p role="status" className="py-16 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (procesos.isError) {
    return (
      <p role="alert" className="py-16 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {procesos.error instanceof ErrorApi
          ? procesos.error.message
          : "No se pudieron cargar los procesos"}
      </p>
    )
  }

  // Las áreas salen de los propios procesos: no hace falta otro permiso para filtrar.
  const areas = new Map<number, string>()
  for (const p of procesos.data) {
    if (p.departamento_id !== null) areas.set(p.departamento_id, p.departamento ?? "")
  }
  const filtrados = procesos.data.filter((p) =>
    area === ""
      ? true
      : area === "sin"
        ? p.departamento_id === null
        : p.departamento_id === Number(area),
  )

  function enviarNuevo(evento: FormEvent) {
    evento.preventDefault()
    crear.mutate()
  }

  return (
    <div>
      {/* Ni título ni frase propios: los pone la cabecera de «Planificación
          estratégica». Aquí arriba sólo van las acciones, a la derecha. Y sin el
          ancho máximo centrado de cuando era su propia pantalla: descolocaría el
          contenido respecto de las pestañas. */}
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        {filtrados.length > 0 && (
          <Exportar
            titulo="Procesos"
            documento={() =>
              documentoDeProcesos(
                filtrados,
                area === ""
                  ? "Todas las áreas"
                  : area === "sin"
                    ? "Sin área"
                    : `Área: ${areas.get(Number(area)) ?? ""}`,
              )
            }
          />
        )}
        <button
          type="button"
          onClick={() => setCreando(true)}
          aria-haspopup="dialog"
          className="boton-primario flex h-9 items-center gap-2 px-3 text-sm font-medium"
        >
          <Plus size={16} aria-hidden="true" />
          Nuevo proceso
        </button>
      </div>
      <KpiProcesos />

      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        <div>
          <h2 className="rotulo mb-2">Procesos</h2>
          {areas.size > 0 && (
            <select
              aria-label="Filtrar por área"
              className="campo mb-2"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              <option value="">Todas las áreas</option>
              {[...areas].map(([id, nombre]) => (
                <option key={id} value={id}>
                  {nombre}
                </option>
              ))}
              <option value="sin">Sin área</option>
            </select>
          )}
          <ListaProcesos
            procesos={filtrados}
            todos={procesos.data}
            elegidoId={proceso?.id}
            onElegir={setElegido}
          />
        </div>

        {proceso !== undefined && (
          <FichaProceso proceso={proceso} procesos={procesos.data} cargos={cargos.data ?? []} />
        )}
      </div>

      <Modal
        abierto={creando}
        titulo="Nuevo proceso"
        descripcion="Un procedimiento de la agencia. Sus pasos se añaden después."
        ancho="sm"
        onCerrar={() => setCreando(false)}
      >
        <form onSubmit={enviarNuevo} aria-label="Nuevo proceso">
          <label htmlFor="pr-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre del proceso
          </label>
          <input
            id="pr-nombre"
            required
            minLength={3}
            className="campo"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="Nacionalizar un despacho marítimo"
          />
          {crear.error && (
            <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
              {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreando(false)}
              className="boton-secundario h-9 px-3 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={crear.isPending}
              className="boton-primario h-9 px-3 text-sm"
            >
              Crear proceso
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
